import React from 'react';
import { svgs } from 'assets';

// --- Warm, guided form palette & styles ---
// Shared between the external (candidate-facing) reference page and the
// admin-side read-only capture target, so both render an identical report.
const form = {
  ink: '#22303f',
  slate: '#6b7d8f',
  paper: '#faf7f0',
  cream: '#fffdf9',
  line: '#e7e0d0',
  teal: '#408996',
  tealDeep: '#2f6771',
  tealTint: 'rgba(64, 137, 150, 0.08)',
  amber: '#c8842d',
  blue: '#007bff',
  blueDeep: '#0062cc',
  blueTint: 'rgba(0, 123, 255, 0.08)'
};

const formStyles = {
  wrapper: {
    background: form.paper,
    minHeight: '100vh',
    padding: '32px 16px 90px'
  },
  shell: { maxWidth: '1100px', margin: '0 auto' },
  formCard: {
    background: form.cream,
    borderRadius: '12px',
    border: `1px solid ${form.line}`,
    boxShadow: '0 1px 2px rgba(20, 30, 40, 0.04)',
    overflow: 'hidden'
  },
  headerCard: {
    background: form.blueTint,
    borderBottom: `1px solid rgba(0, 123, 255, 0.18)`,
    padding: '30px 30px 26px'
  },
  brandBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '22px'
  },
  brandLogo: { width: '52px', height: '52px', flexShrink: 0 },
  brandName: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: form.ink,
    letterSpacing: '0.2px'
  },
  intro: { textAlign: 'center' },
  eyebrow: {
    color: form.tealDeep,
    fontSize: '1.6rem',
    fontWeight: '700',
    letterSpacing: '0.5px',
    marginBottom: '12px'
  },
  subtitle: {
    color: form.ink,
    fontSize: '1.4rem',
    lineHeight: '1.55',
    maxWidth: '620px',
    margin: '0 auto'
  },
  card: {
    padding: '24px 0',
    borderBottom: `1px solid ${form.line}`
  },
  cardLast: {
    padding: '24px 0'
  },
  cardHead: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    marginBottom: '18px'
  },
  cardIndex: {
    flexShrink: 0,
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: form.tealTint,
    color: form.teal,
    fontWeight: '700',
    fontSize: '1.15rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  question: {
    fontSize: '1.35rem',
    fontWeight: '600',
    color: form.ink,
    lineHeight: '1.45'
  },

  // --- Read-only report-specific additions ---
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '999px',
    background: 'rgba(0, 123, 255, 0.12)',
    border: `1px solid rgba(0, 123, 255, 0.35)`,
    color: form.blueDeep,
    fontSize: '1.05rem',
    fontWeight: '700',
    letterSpacing: '0.3px'
  },
  summaryStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    borderBottom: `1px solid ${form.line}`
  },
  summaryCell: {
    padding: '22px 26px',
    borderRight: `1px solid ${form.line}`
  },
  summaryLabel: {
    fontSize: '1.15rem',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: form.slate,
    marginBottom: '6px'
  },
  summaryValue: {
    fontSize: '1.3rem',
    fontWeight: '700',
    color: form.ink,
    wordBreak: 'break-word'
  },
  rowLabel: {
    fontSize: '1.2rem',
    fontWeight: '600',
    letterSpacing: '0.3px',
    color: form.slate,
    marginBottom: '6px'
  },
  questionLabel: {
    fontSize: '1.35rem',
    fontWeight: '600',
    color: form.ink,
    marginBottom: '12px'
  },
  rowValue: {
    fontSize: '1.36rem',
    fontWeight: '600',
    color: form.ink,
    wordBreak: 'break-word'
  },
  answerCard: {
    border: `1px solid ${form.line}`,
    borderRadius: '10px',
    padding: '18px 20px',
    background: '#fbfaf7'
  },
  verdictPill: (active) => ({
    display: 'inline-block',
    padding: '9px 18px',
    borderRadius: '8px',
    fontSize: '1.28rem',
    fontWeight: '700',
    border: `1.5px solid ${active ? form.blue : form.line}`,
    background: active ? form.blue : '#fff',
    color: active ? '#fff' : form.slate
  }),
  commentBlock: {
    marginTop: '14px',
    paddingTop: '14px',
    borderTop: `1px dashed ${form.line}`,
    fontSize: '1.36rem',
    color: form.ink,
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap'
  },
  commentSubLabel: {
    fontSize: '1.2rem',
    fontWeight: '600',
    letterSpacing: '0.3px',
    textTransform: 'none',
    color: form.slate,
    marginBottom: '6px'
  },
  footerNote: {
    marginTop: '10px',
    paddingTop: '20px',
    borderTop: `1px solid ${form.line}`,
    fontSize: '1.2rem',
    color: '#000',
    textAlign: 'center'
  }
};

const formatSubmittedDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-NG', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

/**
 * Read-only, executive-facing rendering of a completed reference submission.
 * Shared by the external candidate-facing page and the admin conclude-check
 * capture target.
 */
const ReferenceReport = ({ submission, extra, employeeName }) => {
  const submittedOn = formatSubmittedDate(submission?.createdAt);

  const profileRows = [
    { label: 'Full Name', value: submission?.fullname },
    { label: 'Occupation', value: extra.occupation },
    { label: 'Address', value: submission?.address },
    { label: 'Telephone Number', value: submission?.phone },
    { label: 'Email Address', value: submission?.email }
  ];

  const assessments = [
    {
      label: 'Relationship with Employee',
      value: extra.question_1_relationship,
      comment: extra.question_1_comments
    },
    {
      label: 'How Long the Referee Has Known the Employee',
      value: extra.question_2_duration,
      comment: extra.question_2_comments
    },
    {
      label: 'Considered Honest?',
      value: extra.question_3_honesty
    },
    {
      label: 'Recommended for Employment?',
      value: extra.question_4_recommend,
      comment: extra.question_4_reason
    },
    {
      label: 'Self-Motivation Level',
      value: extra.question_5_motivation
    }
  ];

  return (
    <div style={formStyles.wrapper}>
      <div style={formStyles.shell}>
        <div style={formStyles.formCard}>
          <div style={formStyles.headerCard}>
            <div style={formStyles.brandBar}>
              <svgs.LOGO style={formStyles.brandLogo} />
              <span style={formStyles.brandName}>Workforce Group</span>
            </div>
            <div style={formStyles.intro}>
              <p style={formStyles.eyebrow}>Reference Verification Report</p>
              <p style={formStyles.subtitle}>
                Reference statement submitted on behalf of {employeeName}
              </p>
              <span style={{ ...formStyles.statusPill, marginTop: '16px' }}>
                <span aria-hidden="true">✓</span>
                Submission Received
              </span>
            </div>
          </div>

          <div style={formStyles.summaryStrip}>
            <div style={formStyles.summaryCell}>
              <div style={formStyles.summaryLabel}>Referee</div>
              <div style={formStyles.summaryValue}>{submission?.fullname || 'N/A'}</div>
            </div>
            <div style={formStyles.summaryCell}>
              <div style={formStyles.summaryLabel}>Relationship</div>
              <div style={formStyles.summaryValue}>
                {extra.question_1_relationship || 'N/A'}
              </div>
            </div>
            <div style={formStyles.summaryCell}>
              <div style={formStyles.summaryLabel}>Recommended</div>
              <div style={formStyles.summaryValue}>
                {extra.question_4_recommend || 'N/A'}
              </div>
            </div>
            <div style={{ ...formStyles.summaryCell, borderRight: 'none' }}>
              <div style={formStyles.summaryLabel}>Submitted</div>
              <div style={formStyles.summaryValue}>{submittedOn || 'N/A'}</div>
            </div>
          </div>

          <div style={formStyles.card}>
            <div style={formStyles.cardHead}>
              <span style={formStyles.cardIndex}>1</span>
              <p style={formStyles.question}>Referee Profile</p>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
                marginLeft: '46px'
              }}
            >
              {profileRows.map((row) => (
                <div key={row.label}>
                  <div style={formStyles.rowLabel}>{row.label}</div>
                  <div style={formStyles.rowValue}>
                    {row.value || <span style={{ color: form.slate }}>N/A</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={formStyles.card}>
            <div style={formStyles.cardHead}>
              <span style={formStyles.cardIndex}>2</span>
              <p style={formStyles.question}>Referee&apos;s Assessment</p>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '14px'
              }}
            >
              {assessments.map((item) => (
                <div style={formStyles.answerCard} key={item.label}>
                  <div style={formStyles.questionLabel}>{item.label}</div>
                  <span style={formStyles.verdictPill(!!item.value)}>
                    {item.value || 'Not answered'}
                  </span>
                  {item.comment && (
                    <div style={formStyles.commentBlock}>
                      <div style={formStyles.commentSubLabel}>Comments</div>
                      {item.comment}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {extra.generalComments && (
            <div style={formStyles.cardLast}>
              <div style={formStyles.cardHead}>
                <span style={formStyles.cardIndex}>3</span>
                <p style={formStyles.question}>General Comments</p>
              </div>
              <div
                style={{
                  ...formStyles.answerCard,
                  whiteSpace: 'pre-wrap',
                  marginLeft: '46px'
                }}
              >
                {extra.generalComments}
              </div>
            </div>
          )}

          <p style={formStyles.footerNote}>
            This document was generated from a verified reference submission and is
            intended for confidential internal review only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReferenceReport;
